<script setup lang="ts">
import { getLocalStorageReac } from "./assets/js/helpers";
import { type Ref } from "vue";
import { useDefaultStore } from "./store";
import { useModalStore } from "./store/modal";
import ModernModal from "./components/shared/modal/ModernModal.vue";
import Toast from "primevue/toast";
import { useWebSocketStore } from "./store/websocket";
import { useToast } from "primevue/usetoast";

const defaultStore = useDefaultStore();
const wsStore = useWebSocketStore();
const modalStore = useModalStore();
const route = useRoute();
const router = useRouter();
const canonicalEl = ref<HTMLLinkElement | null>(null);
const toast = useToast();

const modernModalRef = ref<InstanceType<typeof ModernModal> | null>(null);
const isBootstrapLoaderVisible = computed(() => defaultStore.isAppLoading);

function checkSetAppDarkTheme() {
	const settingReac = getLocalStorageReac(
		"app-color-scheme",
		false,
		"auto",
	) as Ref<any>;
	watch(
		settingReac,
		(isSetting: string) => {
			const prefersDark = window.matchMedia(
				"(prefers-color-scheme: dark)",
			).matches;
			if (isSetting === "dark" || (prefersDark && isSetting !== "light")) {
				document.documentElement.classList.add("dark");
			} else {
				document.documentElement.classList.remove("dark");
			}
		},
		{ immediate: true },
	);
}

watch(
	() => defaultStore.isMobileView,
	(val) => {
		if (val) document.body.classList.add("is-mobile");
		else if (!val) document.body.classList.remove("is-mobile");
	},
);

function onCloseModalComp() {
	if (modernModalRef.value) {
		modernModalRef.value.onClickClose(null, true);
	} else {
		console.error("No modal instance");
	}
}

onBeforeMount(() => {
	checkSetAppDarkTheme();
});

onMounted(async () => {
	canonicalEl.value = document.querySelector('link[rel="canonical"]');
	const timeoutId = window.setTimeout(() => {
		defaultStore.isAppLoading = false;
		defaultStore.markAppReady();
	}, 15_000);

	try {
		defaultStore.isAppLoading = true;
		await router.isReady();
		await defaultStore.getUser();
		wsStore.initWs(defaultStore.playerToken);
		await wsStore.waitUntilOpen();
	} catch (e) {
		console.warn(e);
	} finally {
		window.clearTimeout(timeoutId);
		defaultStore.isAppLoading = false;
		defaultStore.markAppReady();
	}
});

watch(
	() => route.path,
	(newValue, OldValue) => {
		if (canonicalEl.value) {
			const href = canonicalEl.value.href;
			const baseURL = new URL(href).origin;
			canonicalEl.value.setAttribute("href", baseURL + route.path);
		}
	},
);

watch(
	() => defaultStore.errorToastTrigger,
	(newValue, OldValue) => {
		if (newValue) {
			toast.add({
				severity: "error",
				summary: "Error",
				detail:
					defaultStore.errorMessage ??
					"An unexpected error occurred. Please try again.",
				life: 2500,
			});

			defaultStore.errorToastTrigger = false;
		}
	},
);
</script>

<template>
	<router-view
		class="main-application-class"
		:class="{ 'is-mobile': defaultStore.isMobileToggled }"
		:key="route.fullPath"
	></router-view>

	<Transition name="app-bootstrap-fade">
		<div
			v-if="isBootstrapLoaderVisible"
			class="app-bootstrap-loader"
			aria-hidden="true"
		></div>
	</Transition>
	<!-- <RouterView v-slot="{ Component }">
			<div
				style="
					position: fixed;
					top: 0;
					left: 0;
					z-index: 99999;
					background: red;
					color: white;
				"
			>
				{{ String(route.name) }} | {{ route.fullPath }}
			</div>

			<component
				:is="Component"
				:key="route.fullPath"
			/>
		</RouterView> -->

	<enon-browser-detect></enon-browser-detect>
	<modern-modal
		v-if="modalStore.isGlobalModalVisible"
		:key="modalStore.getModalData('comp')"
		ref="modernModalRef"
		:config="modalStore.getModalData('config', {})"
		:form-style="modalStore.getModalData('formStyle', {})"
		@close-modal="modalStore.isGlobalModalVisible = null"
	>
		<template #content="{ slotProps }">
			<component
				:is="modalStore.getModalData('comp')"
				v-bind="slotProps"
				@close-modal="onCloseModalComp"
			></component>
		</template>
	</modern-modal>

	<Toast />
</template>

<style lang="scss">
@import "./assets/css/base.css";

body {
	display: flex;
	overflow-x: hidden;

	&.is-mobile {
		overflow-y: hidden;
	}
}

#app {
	width: 100vw;
	background-color: $dark-gray;
}

.app-bootstrap-loader {
	position: fixed;
	inset: 0;
	z-index: 9999;
	background:
		radial-gradient(circle at center, rgba(255, 255, 255, 0.08), transparent 34%),
		linear-gradient(135deg, rgb(18, 20, 22), rgb(5, 5, 6));
}

.app-bootstrap-fade-leave-active {
	transition: opacity 0.28s ease;
}

.app-bootstrap-fade-leave-to {
	opacity: 0;
}

.category-back-link {
	position: absolute;
	top: 12px;
	left: 12px;
	z-index: 10;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	height: 34px;
	padding: 0 10px;
	border: 1px solid rgba(255, 255, 255, 0.14);
	border-radius: 999px;
	background: rgba(255, 255, 255, 0.12);
	color: rgb(255, 255, 255);
	font-size: 12px;
	font-weight: 700;
	text-decoration: none;
	backdrop-filter: blur(10px);
	box-shadow: 0 12px 30px rgba(0, 0, 0, 0.18);
	cursor: pointer;
	transition:
		background 0.2s ease,
		border-color 0.2s ease,
		transform 0.2s ease;
}

.category-back-link:hover {
	background: rgba(255, 255, 255, 0.16);
	border-color: rgba(255, 255, 255, 0.24);
}

@media (min-width: 641px) {
	.category-back-link {
		top: 20px;
		left: 20px;
		height: 46px;
	}
}
</style>
